import * as Sentry from "@sentry/react";
import { environment as env, isDevelopment } from "./Environment";

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const enableInDev = import.meta.env.VITE_SENTRY_ENABLE_IN_DEV?.toLowerCase() === "true";

  if (!dsn || dsn === "NULL") {
    console.log("[SENTRY] DSN not configured, skipping initialization");
    return;
  }

  if (isDevelopment && !enableInDev) {
    console.log("[SENTRY] Disabled in development (set VITE_SENTRY_ENABLE_IN_DEV=true to enable)");
    return;
  }

  Sentry.init({
    dsn,
    environment: env,
    tags: {
      platform: "frontend",
    },
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          "localhost",
          /^https:\/\/api\.forgeworks\.ai/,
          /^https:\/\/forgeworks\.ai/,
          /^https:\/\/app\.forgeworks\.ai/,
        ],
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: env === "production" ? 0.1 : 1.0,
    replaysSessionSampleRate: env === "production" ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      if (event.exception) {
        const error = hint.originalException;
        if (error instanceof Error && error.message.includes("ResizeObserver")) {
          return null;
        }
        if (error instanceof Error && error.message.includes("Non-Error promise rejection")) {
          return null;
        }
      }

      if (event.request?.query_string) {
        const query = new URLSearchParams(event.request.query_string);
        query.delete("token");
        query.delete("password");
        query.delete("apiKey");
        event.request.query_string = query.toString();
      }

      if (event.request?.headers) {
        delete event.request.headers["authorization"];
        delete event.request.headers["cookie"];
        delete event.request.headers["x-api-key"];
      }

      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((crumb) => {
          if (crumb.data) {
            const sensitiveKeys = ["token", "password", "apiKey", "authorization"];
            sensitiveKeys.forEach((key) => {
              if (crumb.data?.[key]) {
                crumb.data[key] = "[Filtered]";
              }
            });
          }
          return crumb;
        });
      }

      return event;
    },
  });

  console.log("[SENTRY] Initialized successfully (frontend)");
};

export const setSentryUser = (user: {
  id?: string;
  email?: string;
  username?: string;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username || user.email,
  });
};

export const clearSentryUser = () => {
  Sentry.setUser(null);
};

export const captureException = (error: Error, context?: Record<string, any>) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.keys(context).forEach((key) => {
        scope.setContext(key, context[key]);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

export const captureMessage = (message: string, level: Sentry.SeverityLevel = "info") => {
  Sentry.captureMessage(message, level);
};

